import { useLayoutEffect, useMemo, useState } from 'react';

// Fit a line of text into N lines of a box, cutting only BETWEEN words.
// A CSS clamp (-webkit-line-clamp / text-overflow) removes characters until the
// ellipsis fits, which leaves "Officer Report for Incid…"; a title should end on
// a whole word. The box is measured once per layout (every card in a grid has the
// same title width), the words on a canvas with the element's own font.

let ctx = null;
const widthsByFont = new Map();   // font → Map(text → px)

function measurer(font) {
  if (!ctx) ctx = document.createElement('canvas').getContext('2d');
  let cache = widthsByFont.get(font);
  if (!cache) { cache = new Map(); widthsByFont.set(font, cache); }
  return (s) => {
    let w = cache.get(s);
    if (w === undefined) {
      ctx.font = font;
      w = ctx.measureText(s).width;
      cache.set(s, w);
    }
    return w;
  };
}

// A name broken mid-way by the browser wastes up to a glyph at each line end.
const BREAK_SLACK = 12;

// Greedy line breaking at spaces only. The browser also breaks after hyphens, so it
// fits at least what this does — the result is never longer than the box holds.
export function fitWords(text, maxWidth, maxLines, measure) {
  const full = String(text || '').trim();
  const words = full.split(/\s+/).filter(Boolean);
  if (words.length === 0 || !(maxWidth > 0)) return full;
  const space = measure(' ');
  const ellipsis = measure('…');
  let line = 1;
  let x = 0;
  let lineStart = 0;                       // index of the first word on the current line
  for (let i = 0; i < words.length; i += 1) {
    const w = measure(words[i]);
    const next = x === 0 ? w : x + space + w;
    if (next <= maxWidth) { x = next; continue; }
    if (x > 0 && line < maxLines) {        // the word starts the next line
      line += 1; lineStart = i; x = 0; i -= 1;
      continue;
    }
    if (x === 0) {
      // one name wider than the box ("elkocountycew_Audit_Log_for_AXON_Body_2…"): the browser
      // breaks it wherever it must
      const linesLeft = maxLines - line + 1;
      const lines = Math.ceil(w / maxWidth);
      if (lines <= linesLeft) { line += lines - 1; x = w - (lines - 1) * maxWidth; continue; }
      // longer than the room left: end it after the last of its parts (joined by _ - .) that fits
      const room = linesLeft * maxWidth - (linesLeft - 1) * BREAK_SLACK - ellipsis;
      let cutAt = 0;
      for (const m of words[i].matchAll(/[_\-.]+/g)) {
        if (m.index > 0 && measure(words[i].slice(0, m.index)) <= room) cutAt = m.index; else if (m.index > 0) break;
      }
      if (cutAt === 0) return full;        // nothing to hold on to: leave the cut to the CSS clamp
      return `${[...words.slice(0, i), words[i].slice(0, cutAt)].join(' ')}…`;
    }
    // out of room: end on the last whole word that leaves space for the ellipsis
    let end = i;
    while (end > lineStart && x + ellipsis > maxWidth) {
      end -= 1;
      x -= measure(words[end]) + (end > lineStart ? space : 0);
    }
    if (end === 0) return full;
    return `${words.slice(0, end).join(' ').replace(/[\s\-–—·,:;]+$/, '')}…`;
  }
  return full;
}

// Returns fit(text) for the elements matching `selector` under `rootRef`. Until the
// box is measured (first paint) it returns the text unchanged and the CSS clamp holds.
export default function useWordFit(rootRef, selector, lines = 2, layoutKey = '') {
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const read = () => {
      const el = root.querySelector(selector);
      if (!el) return;
      const cs = getComputedStyle(el);
      // 4 px steps: a window being dragged does not re-fit every card on every frame
      const width = Math.floor(el.clientWidth / 4) * 4;
      const font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      if (width > 0) setBox((cur) => (cur && cur.width === width && cur.font === font ? cur : { width, font }));
    };
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(root);
    return () => ro.disconnect();
  }, [rootRef, selector, layoutKey]);
  return useMemo(() => {
    if (!box) return (text) => text;
    const measure = measurer(box.font);
    return (text) => fitWords(text, box.width, lines, measure);
  }, [box, lines]);
}
